import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Layout, Space, Spin, Tabs, TabsProps, theme, Typography } from 'antd';

import { Tables } from '@app/web-components';

import { NotFoundPage } from '../../common';
import { useFindBundleById } from '../data';

import { DetailTab } from './detail-tab';
import { Header } from './header';
import { StartPullBundleAccessMembershipsButton } from './start-pull-bundle-access-memberships-button';
import { StartPullBundleSubscriptionAccessEligibilityButton } from './start-pull-bundle-subscription-access-eligibility-button';
import { StartPullDmsTicketsButton } from './start-pull-dms-tickets-button';
import { SubscriptionAccessTab } from './subscription-access-tab';

export const TAB_SEARCH_PARAM = 'tab';
export const DETAIL_TAB = 'detail';
export const SUBSCRIPTION_ACCESS_TAB = 'subscription-access';

export const ManagePage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({ TAB_SEARCH_PARAM: DETAIL_TAB });
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const bundleId = Number(id);
  const { data: bundle, error } = useFindBundleById({ bundleId });
  const { parentRef, toolbarRef, getTableHeight } = Tables.useReactiveTableHeight();

  if (error) {
    return (
      <NotFoundPage subTitle="Sorry, this bundle does not exist or was deleted.">
        <Button type="primary" onClick={() => navigate('../')}>
          Back to Bundles
        </Button>
      </NotFoundPage>
    );
  }

  const tabs: TabsProps['items'] = [
    {
      key: DETAIL_TAB,
      label: `Detail`,
      children: <DetailTab bundleId={bundleId} />,
    },
    {
      key: SUBSCRIPTION_ACCESS_TAB,
      label: `Subscription Access`,
      children: <SubscriptionAccessTab bundleId={bundleId} tableHeight={getTableHeight([270], 130)} />,
    },
  ];

  const handleTabChange = (activeKey: string) => {
    setSearchParams({ tab: activeKey });
  };

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: <Link to="../">Bundles</Link> }, { title: 'Manage' }]}
      />
      <Layout.Content
        ref={parentRef}
        style={{
          paddingTop: 24,
          paddingLeft: 24,
          paddingRight: 24,
          margin: 0,
          height: '100%',
          minHeight: 0,
          background: token.colorBgContainer,
          overflow: 'auto',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {bundle ? bundle.name : <Spin size="small" />}
            </Typography.Title>
            <Space>
              <Button icon={<EditOutlined />} onClick={() => navigate(`../${bundle?.id}/edit`)}>
                Edit
              </Button>
              {bundle?.pullSubscriptionEnabled ? (
                <StartPullBundleAccessMembershipsButton bundleId={bundle.id} />
              ) : undefined}
              <StartPullBundleSubscriptionAccessEligibilityButton bundleId={bundleId} />
              <StartPullDmsTicketsButton />
            </Space>
          </Space>
          <div ref={toolbarRef} style={{ marginTop: 10, width: '100%' }}>
            <Header bundleId={bundleId} />
          </div>
          <Tabs
            style={{ marginTop: 5 }}
            defaultActiveKey={searchParams.get(TAB_SEARCH_PARAM) || DETAIL_TAB}
            items={tabs}
            onChange={handleTabChange}
          />
        </Space>
      </Layout.Content>
    </>
  );
};
